/**
 * GridPanel Component
 *
 * Renders panel actions on a fixed grid, each cell positioned by explicit
 * row/col. Used when the phone layout must mirror a board shown on the TV.
 */

import React, { useState } from "react"
import { GridPanelDefinition } from "@/types/actions"
import { HapticFeedbackService } from "@/services/hapticFeedback"
import { ActionButton } from "../ActionButton"
import { Card } from "@/components/ui/card"
import { useQuestionTimer } from "@/hooks/useQuestionTimer"

interface GridPanelProps {
  panel: GridPanelDefinition
  requestId: string
  onActionClick: (actionId: string) => void
  disabled?: boolean
  /**
   * Action IDs found in the phone info HTML (data-action-id).
   * In display_mode="html_bridge" those cells are already clickable in the
   * HTML, so the grid does not render duplicate buttons for them.
   */
  bridgedActionIds?: ReadonlySet<string>
}

/**
 * GridPanel - action buttons laid out on a rows x columns grid.
 *
 * Cells are positioned explicitly (no auto-placement), so a sparse or
 * unordered `cells` list still lands exactly where the game mode asked —
 * positional fidelity with the TV board is the point of this panel.
 */
export const GridPanel: React.FC<GridPanelProps> = ({
  panel,
  requestId,
  onActionClick,
  disabled = false,
  bridgedActionIds,
}) => {
  const [loading, setLoading] = useState(false)
  const [selectedAction, setSelectedAction] = useState<string | null>(null)
  const { timeLeft } = useQuestionTimer(panel.timeout_seconds, requestId)

  const visibleCells =
    panel.display_mode === "html_bridge" && bridgedActionIds
      ? panel.cells.filter((cell) => !bridgedActionIds.has(cell.action.id))
      : panel.cells

  const handleClick = async (actionId: string) => {
    if (disabled || loading) return

    setLoading(true)
    setSelectedAction(actionId)
    HapticFeedbackService.notification()

    try {
      await onActionClick(actionId)
      HapticFeedbackService.success()
    } catch (err) {
      console.error("[GridPanel] Error in action click:", err)
      HapticFeedbackService.error()
      setSelectedAction(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card
      className="border-t-2 border-primary overflow-hidden flex flex-col min-h-0"
      data-testid="grid-panel"
      data-request-id={requestId}
    >
      {/* The card itself must not scroll — its border and rounded corners have to
          stay in view; everything inside it scrolls as one area instead. */}
      <div className="p-6 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Header */}
        <div className="mb-4 flex justify-between items-end">
          <div>
            {panel.title && (
              <h2 className="text-sm font-semibold text-primary mb-1">
                {panel.title}
              </h2>
            )}
            {panel.description && (
              <p className="text-muted-foreground text-xs leading-relaxed max-w-[80%]">
                {panel.description}
              </p>
            )}
          </div>

          {timeLeft !== null && timeLeft >= 0 && (
            <div
              className="flex items-center gap-2 px-2 py-1 bg-muted rounded border border-border"
              data-testid="grid-timer"
            >
              <div className="w-1.5 h-1.5 bg-secondary rounded-full animate-ping" />
              <span className="text-[8px] text-muted-foreground font-bold">
                {timeLeft}s
              </span>
            </div>
          )}
        </div>

        {/* Grid — inline styles on purpose: Tailwind JIT cannot generate
            runtime-composed class names like `grid-cols-${columns}`. */}
        {visibleCells.length > 0 && (
          <div
            className="grid gap-2"
            data-testid="grid-panel-cells"
            style={{
              gridTemplateColumns: `repeat(${panel.columns}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${panel.rows}, minmax(0, auto))`,
            }}
          >
            {visibleCells.map((cell) => (
              <div
                key={`${cell.row}-${cell.col}`}
                className="grid"
                data-testid={`grid-cell-${cell.row}-${cell.col}`}
                style={{ gridRow: cell.row + 1, gridColumn: cell.col + 1 }}
              >
                <ActionButton
                  action={cell.action}
                  onClick={() => handleClick(cell.action.id)}
                  loading={loading && selectedAction === cell.action.id}
                  disabled={disabled || loading}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}
